import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  BookOpen, Building2, Star, Loader2,
  Save, ChevronLeft, RefreshCcw, FileText, CalendarDays, Sparkles,
  Lock, Unlock,
} from "lucide-react";
import { adminApi } from "../api";
import { toast } from "sonner";
import CursoTabsNav from "../components/CursoTabsNav";
import { EntidadeFormadoraAviso, EntidadeFormadoraBulk } from "../components/EntidadeFormadora";

const toDateInput = (s) => {
  if (!s) return "";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch { return ""; }
};

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return s; }
};

// Layout configuration per tab. Each item: {key, label, type, span, hint}.
const TABS = [
  {
    id: "geral", icon: BookOpen, title: "Geral",
    fields: [
      { key: "NomeCurso", label: "Nome do Curso", type: "text", span: 2 },
      { key: "InCompany", label: "Tipo de Curso", type: "select", span: 2,
        options: [{ value: "N", label: "Público em geral" }, { value: "S", label: "In-company (empresa)" }],
        hint: "Os dados de Empresa só se aplicam a cursos in-company." },
      { key: "ID_Produto", label: "ID Produto", type: "number", span: 1, hint: "FK produto.Tinf_Produto" },
      { key: "Edicao", label: "Edição", type: "number", span: 1 },
      { key: "TurmaCodigo", label: "Código da Turma (fxalunos)", type: "text", span: 1 },
      { key: "CursoNoSigo", label: "CursoNoSigo (DGERT)", type: "text", span: 1 },
      { key: "Local", label: "Local", type: "text", span: 2 },
      { key: "DataInicio", label: "Data Início", type: "date", span: 1 },
      { key: "DataFim", label: "Data Fim", type: "date", span: 1 },
      { key: "CargaHoraria", label: "Carga Horária (h)", type: "number", span: 1 },
      { key: "DataRealizacaoTexto", label: "Data de Realização (texto)", type: "text", span: 1,
        hint: "Ex: \"26, 27, 28 Janeiro 2024\"" },
      { key: "Horario", label: "Horário (resumo)", type: "textarea", span: 2 },
      { key: "Sessoes", label: "Sessões (descrição)", type: "textarea", span: 2 },
      { key: "Formador", label: "Formador (nome)", type: "text", span: 2 },
      { key: "AreaTexto", label: "Área de Formação (DGERT)", type: "text", span: 2,
        hint: "Ex: \"090 - Desenvolvimento Pessoal\"" },
    ],
  },
  {
    id: "doc", icon: FileText, title: "Documentos & Entidade",
    fields: [
      { key: "EntidadeFormadora", label: "Entidade Formadora", type: "text", span: 1 },
      { key: "LocalEntidade", label: "Local da Entidade", type: "text", span: 1 },
      { key: "MoradaContrato", label: "Morada do Contrato", type: "textarea", span: 2 },
      { key: "FormacaoInstalacoes", label: "Formação nas Instalações", type: "text", span: 2 },
      { key: "DataContrato", label: "Data do Contrato", type: "date", span: 1 },
      { key: "SumarioFim", label: "Sumário Fim (texto livre)", type: "textarea", span: 2 },
    ],
  },
  {
    id: "empresa", icon: Building2, title: "Empresa (B2B)",
    fields: [
      { key: "EmpresaNome", label: "Nome da Empresa", type: "text", span: 2 },
      { key: "EmpresaNif", label: "NIF da Empresa", type: "text", span: 1 },
      { key: "EmpresaRepresentadaPor", label: "Representada por", type: "text", span: 1 },
      { key: "EmpresaQualidade", label: "Na qualidade de", type: "text", span: 2,
        hint: "Ex: \"Diretora de Recursos Humanos\"" },
      { key: "EmpresaSede", label: "Sede da Empresa", type: "textarea", span: 2 },
    ],
  },
  {
    id: "dtp", icon: CalendarDays, title: "Datas DTP",
    fields: [
      { key: "DataDtp1", label: "Data DTP 1", type: "date", span: 1 },
      { key: "DataDtp3", label: "Data DTP 3", type: "date", span: 1 },
      { key: "DataDtp4", label: "Data DTP 4", type: "date", span: 1 },
    ],
  },
  {
    id: "aval", icon: Star, title: "Avaliação",
    fields: [
      { key: "Aval1", label: "Avaliação 1", type: "number", span: 1, step: "0.01" },
      { key: "Aval2", label: "Avaliação 2", type: "number", span: 1, step: "0.01" },
    ],
  },
];

const ALL_EDITABLE_KEYS = TABS.flatMap((t) => t.fields.map((f) => f.key));


export default function AdminCursoEdit() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cursoId = searchParams.get("curso") || "";

  const [cursos, setCursos] = useState([]);
  const [cursosLoading, setCursosLoading] = useState(true);
  const [includePast, setIncludePast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [curso, setCurso] = useState(null);
  const [form, setForm] = useState({});
  const [tab, setTab] = useState("geral");
  const [dirty, setDirty] = useState(false);

  // Load curso picker list
  const loadCursos = useCallback(async () => {
    setCursosLoading(true);
    try {
      const r = includePast
        ? await adminApi.cursosListIncludingPast()
        : await adminApi.cursosListActive();
      setCursos(r.data || []);
    } catch (e) { toast.error(e.message || "Erro a carregar cursos"); }
    finally { setCursosLoading(false); }
  }, [includePast]);

  useEffect(() => { loadCursos(); }, [loadCursos]);

  // Load the selected curso (full payload)
  const loadCurso = useCallback(async () => {
    if (!cursoId) { setCurso(null); setForm({}); return; }
    setLoading(true);
    try {
      const r = await adminApi.cursoFullGet(cursoId);
      setCurso(r.curso);
      // Pre-populate form with current values, converting dates
      const f = {};
      for (const k of ALL_EDITABLE_KEYS) {
        const v = r.curso?.[k];
        if (v === null || v === undefined) {
          f[k] = "";
        } else if (k.startsWith("Data") && k !== "DataRealizacaoTexto") {
          f[k] = toDateInput(v);
        } else {
          f[k] = v;
        }
      }
      f.InCompany = r.curso?.InCompany === "S" ? "S" : "N";
      setForm(f);
      setDirty(false);
    } catch (e) { toast.error(e.message || "Erro a carregar curso"); }
    finally { setLoading(false); }
  }, [cursoId]);

  useEffect(() => { loadCurso(); }, [loadCurso]);

  const setField = (k, v) => {
    setForm((s) => ({ ...s, [k]: v }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!cursoId) return;
    setSaving(true);
    try {
      // Normalize payload: convert empty strings to null, parse numbers/dates
      const payload = {};
      for (const k of ALL_EDITABLE_KEYS) {
        const v = form[k];
        if (v === "" || v === undefined) {
          payload[k] = null;
        } else if (["ID_Produto", "Edicao", "CargaHoraria"].includes(k)) {
          payload[k] = Number(v);
        } else if (["Aval1", "Aval2"].includes(k)) {
          payload[k] = Number(v);
        } else {
          payload[k] = v;
        }
      }
      await adminApi.cursoFullUpdate(cursoId, { curso: payload });
      toast.success("Curso guardado.");
      setDirty(false);
      loadCurso();
    } catch (e) {
      toast.error(e.message || "Erro a guardar");
    } finally { setSaving(false); }
  };

  const [togglingEstado, setTogglingEstado] = useState(false);
  const [estadosList, setEstadosList] = useState([]);
  useEffect(() => { adminApi.cursoEstados().then(setEstadosList).catch(() => {}); }, []);
  // A tab "Empresa (B2B)" só faz sentido em cursos in-company.
  const visibleTabs = TABS.filter((t) => t.id !== "empresa" || form.InCompany === "S");
  useEffect(() => {
    if (tab === "empresa" && form.InCompany !== "S") setTab("geral");
  }, [form.InCompany, tab]);
  const handleChangeEstado = async (novoEstado) => {
    if (!cursoId || !curso || !novoEstado || novoEstado === (curso.ID_EstadoCurso || "").toUpperCase()) return;
    const label = (estadosList.find((e) => e.id === novoEstado) || {}).label || novoEstado;
    if (!window.confirm(`Alterar o estado do curso para "${label}"?`)) return;
    setTogglingEstado(true);
    try {
      await adminApi.cursoSetEstado(cursoId, novoEstado);
      toast.success(`Estado alterado para "${label}".`);
      loadCurso();
    } catch (e) {
      toast.error(e.message || "Erro ao alterar o estado do curso.");
    } finally { setTogglingEstado(false); }
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="admin-curso-edit">
      <Link to="/admin" className="text-xs text-[#6B7280] hover:text-[#0F1829] inline-flex items-center gap-1 mb-3">
        <ChevronLeft size={12} /> Dashboard
      </Link>

      <header className="mb-6 flex items-start gap-3 justify-between flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white">
            <BookOpen size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
              Manutenção de Cursos
            </h1>
            <p className="text-sm text-[#4B5563]">
              Editor amigável com 5 tabs para todos os campos de{" "}
              <code className="text-[11px] bg-[#FAF8F2] border border-[#E6E1D6] px-1 rounded">curso.Tinf_Curso</code>.
            </p>
          </div>
        </div>
      </header>

      <CursoTabsNav cursoId={cursoId} current="geral"
        cursoLabel={curso ? `${curso.NomeCurso || curso.NomeProduto || `Produto ${curso.ID_Produto}`} • #${curso.ID_Curso}` : undefined} />

      <EntidadeFormadoraAviso cursoId={cursoId} />
      <EntidadeFormadoraBulk onSaved={loadCurso} />

      {/* Picker (sempre presente para trocar de curso) */}
      <section className="mb-4 p-4 bg-white border border-[#E6E1D6] rounded-md">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-1">
              Curso
            </label>
            <select
              value={cursoId}
              onChange={(e) => setSearchParams(e.target.value ? { curso: e.target.value } : {})}
              disabled={cursosLoading}
              className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm bg-white"
              data-testid="curso-picker"
            >
              <option value="">— Escolher curso —</option>
              {cursos.map((c) => (
                <option key={c.ID_Curso} value={c.ID_Curso}>
                  {c.NomeCurso || c.Descricao || `Produto ${c.ID_Produto}`} • #{c.ID_Curso}{c.ID_Produto != null ? ` (Produto #${c.ID_Produto})` : ""} • {c.Local || "—"} • {fmtDate(c.DataInicio)}
                </option>
              ))}
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-[#6E6754]">
            <input
              type="checkbox"
              checked={includePast}
              onChange={(e) => setIncludePast(e.target.checked)}
              data-testid="toggle-include-past"
            />
            Mostrar passados
          </label>
        </div>
      </section>

      {!cursoId ? (
        <div className="p-8 text-center text-[#978D78] bg-white border border-[#E6E1D6] rounded-md">
          Escolhe um curso para editar.
        </div>
      ) : loading ? (
        <div className="p-8 text-center text-[#978D78] bg-white border border-[#E6E1D6] rounded-md">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> A carregar…
        </div>
      ) : !curso ? (
        <div className="p-8 text-center text-red-700 bg-white border border-red-200 rounded-md">
          Curso #{cursoId} não encontrado.
        </div>
      ) : (
        <div className="bg-white border border-[#E6E1D6] rounded-md overflow-hidden">
          {/* Read-only header com info chave */}
          <div className="px-5 py-3 border-b border-[#E6E1D6] bg-[#FAF8F2] flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm flex items-center gap-2 flex-wrap">
              <strong className="text-[#1B1B1B]">#{curso.ID_Curso}</strong>
              {curso.ID_Produto != null && (
                <span className="text-[10px] font-mono text-[#978D78]">(Produto #{curso.ID_Produto})</span>
              )}
              {curso.NomeProduto && (
                <span className="text-[#6E6754]">({curso.NomeProduto})</span>
              )}
              {(curso.ID_EstadoCurso || "").toUpperCase() === "E" ? (
                <span
                  data-testid="curso-estado-badge"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#F5E7C8] text-[#6F4F1D] border border-[#E6D6A8]"
                >
                  <Lock size={10} /> Encerrado
                </span>
              ) : (
                <span
                  data-testid="curso-estado-badge"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#E6F4EA] text-[#2E5E38] border border-[#B6DFC1]"
                >
                  <Unlock size={10} /> Aberto ({curso.ID_EstadoCurso || "?"})
                </span>
              )}
              <span className="text-[12px] text-[#978D78]">Activo <code>{curso.Activo}</code></span>
            </div>
            <div className="flex items-center gap-2">
              {dirty && (
                <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                  Alterações por guardar
                </span>
              )}
              <label className="inline-flex items-center gap-1.5 text-xs text-[#6E6754]">
                Estado:
                <select
                  value={(curso.ID_EstadoCurso || "").toUpperCase()}
                  onChange={(e) => handleChangeEstado(e.target.value)}
                  disabled={togglingEstado || saving}
                  data-testid="curso-estado-select"
                  className="px-2 py-1.5 text-xs border border-[#E6E1D6] rounded bg-white disabled:opacity-40"
                >
                  {estadosList.map((e) => (
                    <option key={e.id} value={e.id}>{e.label}</option>
                  ))}
                </select>
                {togglingEstado && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              </label>
              <button
                onClick={loadCurso}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-[#E6E1D6] rounded hover:bg-white"
                data-testid="btn-reload"
                disabled={saving}
              >
                <RefreshCcw className="w-3.5 h-3.5" /> Recarregar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !dirty}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#A8843C] text-white rounded hover:bg-[#8E6E2A] disabled:opacity-40"
                data-testid="btn-save"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Guardar
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-5 pt-2 border-b border-[#E6E1D6] flex flex-wrap gap-1">
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 ${
                    tab === t.id
                      ? "border-[#A8843C] text-[#A8843C] font-semibold"
                      : "border-transparent text-[#6E6754] hover:text-[#1B1B1B]"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {t.title}
                </button>
              );
            })}
          </div>

          {/* Fields */}
          <div className="p-5">
            {visibleTabs.filter((t) => t.id === tab).map((t) => (
              <div key={t.id} className="grid grid-cols-2 gap-4">
                {t.fields.map((f) => (
                  <Field key={f.key} field={f}
                    value={form[f.key] ?? ""}
                    onChange={(v) => setField(f.key, v)}
                  />
                ))}
              </div>
            ))}
          </div>

          {/* Footer sticky save */}
          <footer className="px-5 py-3 border-t border-[#E6E1D6] bg-[#FAF8F2] flex justify-between items-center">
            <span className="text-[11px] text-[#978D78] flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              Auto-detecta tipos de campo. Datas são gravadas em formato ISO.
            </span>
            <button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#A8843C] text-white rounded hover:bg-[#8E6E2A] disabled:opacity-40"
              data-testid="btn-save-footer"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar alterações
            </button>
          </footer>
        </div>
      )}
    </div>
  );
}


function Field({ field, value, onChange }) {
  const { key: k, label, type, span = 1, hint, step } = field;
  const id = `field-${k.toLowerCase()}`;
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label htmlFor={id} className="block text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-1">
        {label}
      </label>
      {type === "textarea" ? (
        <textarea
          id={id}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm font-sans"
          data-testid={id}
        />
      ) : type === "select" ? (
        <select
          id={id}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm bg-white"
          data-testid={id}
        >
          {(field.options || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          type={type}
          step={step}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
          data-testid={id}
        />
      )}
      {hint && <p className="text-[11px] text-[#978D78] mt-1">{hint}</p>}
    </div>
  );
}
